import { IExecutionOptions } from '../models/query';
import { ConditionalHelper } from '../helpers/ConditionalHelper';
import { IDocument } from '../models/api/search';
import { isArrayLike } from '../utils/isArrayLike';
import { Query } from './Query';
import { get } from '../utils';
import { groupBy } from '../utils/collection';
import { isNullOrEmpty } from '../utils/typeChecks';
import { IQueryOptionsDocuments } from '../models/query/options';
import { BaseCollectionQuery } from './BaseCollectionQuery';

export class DocumentQuery extends BaseCollectionQuery<IQueryOptionsDocuments> {
	initialize(executionOptions: IExecutionOptions) {
		this.fillKeyFromQuery('key', 'keyQuery', executionOptions);
	}
	getOptions(): IQueryOptionsDocuments {
		const { entityType, entityConditional, sort, index, startIndex, endIndex, collectionItemConditional, collectionItemValueConditional, documentPriority, fields, key, keyQuery, childKey, entityKey, entityIndex, options = {} } = this.queryOptions;
		return {
			entityType,
			entityConditional,
			sort,
			index,
			startIndex,
			endIndex,
			collectionItemConditional,
			collectionItemValueConditional,
			documentPriority,
			fields,
			childKey,
			key,
			keyQuery,
			entityKey,
			entityIndex,
			...(options.documents || {}),
		};
	}
	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		const { collectionItemConditional, collectionItemValueConditional, index = -1, startIndex = -1, documentPriority } = this.options;

		let documents: IDocument[] = [];
		if (this.options.entityType) {
			documents = this.getSubCollection(executionOptions);
		}
		else {
			const context = Query.getContext(executionOptions, this.queryOptions);
			if (context) {
				const { globalDocuments } = context;
				documents = globalDocuments;
			}
		}

		// this.getSubCollection could return undefined
		if (documents) {
			const conditionalHelper = new ConditionalHelper({ conditions: [] }, this.translationHelper);
			documents = documents.filter((document) => {
				return !collectionItemConditional ||
					conditionalHelper.matches(document, collectionItemConditional, executionOptions);
			});

			// Check against document priority
			if (documentPriority && !isNullOrEmpty(documentPriority.joinFields)) {
				const priorityDocuments: IDocument[] = [];

				const { priorities, joinFields, ignoreNonPriority = false } = documentPriority;
				const groupedDocuments = groupBy(documents, (document: IDocument) => {
					return joinFields.map(j => get(document, j)).join('-');
				});

				for (const key of Object.keys(groupedDocuments)) {
					const currentDocumentList = groupedDocuments[key];
					let foundPriorityDocuments = false;
					for (const priority of priorities) {
						const currentPriorityDocuments = currentDocumentList.filter(document => conditionalHelper.matchesWithOptions({
							executionOptions,
							instance: document,
							conditional: priority,
						}));
						if (!isNullOrEmpty(currentPriorityDocuments)) {
							priorityDocuments.push(...currentPriorityDocuments);
							foundPriorityDocuments = true;
							break;
						}
					}
					if (!foundPriorityDocuments && !ignoreNonPriority) {
						priorityDocuments.push(...currentDocumentList);
					}
				}
				documents = priorityDocuments;
			}

			// Set up the _fields property with the properties we want
			let response: any = documents.map((document) => {
				const documentResult = this.getDocumentResult(document);
				if (documentResult === document) {
					(document as any)._fields = Object.assign({}, documentResult);
				}
				else {
					(document as any)._fields = documentResult;
				}
				return document;
			});

			const matchingValues = documents.filter(value => !collectionItemValueConditional || conditionalHelper.matchesWithOptions({
				executionOptions,
				instance: value,
				conditional: collectionItemValueConditional,
			}));

			if (matchingValues && matchingValues.length > 0) {
				response = this.getItem(executionOptions, matchingValues);
			}

			if ((index > -1 || startIndex > -1) && isArrayLike(response)) {
				response = this.getItem(executionOptions, response);
			}

			return response;
		}
		return [] as any;
	}
	getDocumentResult<T>(document: IDocument): T {
		const { fields } = this.options;
		if (!fields || fields.length === 0) {
			const { key, childKey } = this.options;
			return this.getDocumentField(document, key, childKey) as any;
		}

		const documentResult: any = {};
		for (const field of fields) {
			const { name, key, childKey } = field;
			documentResult[name] = this.getDocumentField(document, key, childKey);
		}
		return documentResult;
	}
	getDocumentField<T>(document: IDocument, key: string = null, childKey: string = null): T {
		if (document) {
			if (key) {
				const { meta, inferred } = document;
				if (key === 'meta' && meta) {
					return childKey && meta[childKey] && meta[childKey].value;
				}
				if (key === 'inferred') {
					return childKey && inferred && inferred[childKey];
				}
				return document[key];
			}
			return document as any;
		}
		return;
	}
}